// src/stores/settings/storage.store.js
import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import StorageService from "@/services/settings/storage.service";
import { toast } from "@/utils/toast";

export const useStorageSettingsStore = defineStore("storageSettings", () => {
  const loading = ref(false);
  const driver = ref("local"); // local | s3

  const aws = reactive({
    access_key: "",
    bucket: "",
    region: "",
    base_url: "",
  });

  async function fetchSettings() {
    loading.value = true;
    try {
      const res = await StorageService.get();
      driver.value = res.data?.driver || "local";

      const awsData = res.data?.aws || {};
      aws.access_key = awsData.access_key || "";
      aws.bucket = awsData.bucket || "";
      aws.region = awsData.region || "";
      aws.base_url = awsData.base_url || "";
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to load storage settings",
        "error"
      );
    } finally {
      loading.value = false;
    }
  }

 async function setDriver(newDriver) {
    loading.value = true;
    try {
      const res = await StorageService.updateDriver(newDriver);
      driver.value = res.data?.driver || newDriver;
      toast("Storage bucket updated", "success");
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to update storage bucket",
        "error"
      );
      throw e; // so the component can react if needed
    } finally {
      loading.value = false;
    }
  }
  /**
   * Save AWS settings for this company.
   * `payload.secret_key` may be omitted/undefined to keep existing key.
   */
  async function saveAwsSettings(payload) {
    loading.value = true;
    try {
      const res = await StorageService.updateAws(payload);

      // Backend returns latest non-secret values – sync them to state.
      const awsData = res.data || {};
      aws.access_key = awsData.access_key || aws.access_key;
      aws.bucket = awsData.bucket || aws.bucket;
      aws.region = awsData.region || aws.region;
      aws.base_url = awsData.base_url || aws.base_url;

      toast("AWS settings saved", "success");
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to save AWS settings",
        "error"
      );
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    driver,
    aws,
    fetchSettings,
    setDriver,
    saveAwsSettings,
  };
});
